/**
 * Version Workflow API — Conversational planner + optimization workflow.
 *
 * Endpoints consumed:
 *  - POST /api/evals/optimization-workflows/{orgId}/prompt-improvements/draft
 *  - POST /api/evals/optimization-workflows/{orgId}/prompt-improvements/generate-changes
 *  - POST /api/evals/optimization-workflows/{orgId}/start
 *  - GET  /api/evals/optimization-workflows/{orgId}/workflows/{batchId}
 *  - POST /api/evals/optimization-workflows/{orgId}/workflows/{batchId}/apply
 */
import apiClient, { type ApiResponse } from './client';

// ─── Planner types ──────────────────────────────────────────────────────────

export interface PlannerMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface DraftPlan {
  request_summary: string;
  focus_summary: string;
  unchanged_summary: string;
  target_areas: string[];
  workflow_request: Record<string, unknown>;
}

export interface DraftRequest {
  messages: PlannerMessage[];
  state?: Record<string, unknown> | null;
  optimization_intent?: 'score_driven' | 'targeted_revision' | null;
  mode?: 'batch_simulation' | 'live_traces';
  dataset_names?: string[] | null;
  auto_discover_datasets?: boolean;
  pause_before_apply?: boolean;
  enable_ab_test?: boolean;
  run_custom_evaluators?: boolean;
  select_criteria_keys?: string[] | null;
  score_threshold?: number;
  max_changes?: number;
  eval_scores?: { name: string; score: number }[];
}

export interface DraftResponse {
  status: 'clarifying' | 'ready' | 'out_of_scope';
  assistant_message: string;
  state: Record<string, unknown>;
  draft_plan?: DraftPlan | null;
  suggestion_labels?: string[];
}

// ─── Workflow types ─────────────────────────────────────────────────────────

export interface WorkflowStartResponse {
  batch_id: string;
  status: string;
  message: string;
}

export interface FailurePattern {
  pattern_id: string;
  pattern_name: string;
  description: string;
  evaluators_affected: string[];
  severity: string | null;
  example_reasoning: string[];
}

export interface PromptChange {
  change_id: string;
  action: string;
  section_name: string;
  rationale: string;
  original_text: string | null;
  new_text: string | null;
}

export interface DiffEntry {
  line_number: number;
  change_type: string;
  original_line: string | null;
  new_line: string | null;
}

export interface ReviewArtifacts {
  feedback_summary: string | null;
  failure_patterns: FailurePattern[];
  changes_summary: string | null;
  correction_warnings: string[];
  proposed_changes: PromptChange[];
  diff_summary: string | null;
  diff_entries: DiffEntry[];
  original_prompt: string | null;
  updated_prompt: string | null;
  original_prompt_version: string | null;
}

export interface ABTestSummary {
  ab_test_id: string | null;
  result: string | null;
  production_version: string | null;
  staging_version: string | null;
  production_overall_avg: number | null;
  staging_overall_avg: number | null;
  per_evaluator: {
    evaluator_name: string;
    production_avg: number;
    staging_avg: number;
    delta: number;
    delta_pct: number;
  }[];
}

export interface BaselineScoreSummary {
  score_averages: Record<string, number | null>;
  total_items: number | null;
  scored_items: number | null;
  trace_count: number | null;
  low_score_count: number | null;
}

export interface WorkflowStatus {
  batch_id: string;
  organization_id: string;
  status: string;
  dataset_names: string[];
  optimization_run_id: string | null;
  prompt_name: string | null;
  new_prompt_version: string | null;
  diff_summary: string | null;
  ab_test_summary: ABTestSummary | null;
  aggregate_metrics: Record<string, unknown> | null;
  workflow_config: Record<string, unknown> | null;
  baseline_scores: BaselineScoreSummary | null;
  post_change_scores: BaselineScoreSummary | null;
  review_artifacts: ReviewArtifacts | null;
  started_at: string | null;
  completed_at: string | null;
  error: string | null;
}

export type ApplyAction = 'save' | 'run_scores' | 'run_ab_test';

export interface ApplyRequest {
  updated_prompt?: string | null;
  action?: ApplyAction | null;
  enable_ab_test?: boolean | null;
}

// ─── Service ────────────────────────────────────────────────────────────────

const PREFIX = '/api/evals/optimization-workflows';

const versionWorkflowService = {
  /** Send a planner conversation turn. Loop until status === 'ready'. */
  draft: (
    orgId: string,
    body: DraftRequest,
  ): Promise<ApiResponse<DraftResponse>> =>
    apiClient.post<DraftResponse>(
      `${PREFIX}/${orgId}/prompt-improvements/draft`,
      body,
    ),

  /** Start the targeted-revision preview flow that generates prompt changes first. */
  generateChanges: (
    orgId: string,
    workflowRequest: Record<string, unknown>,
  ): Promise<ApiResponse<WorkflowStartResponse>> =>
    apiClient.post<WorkflowStartResponse>(
      `${PREFIX}/${orgId}/prompt-improvements/generate-changes`,
      workflowRequest,
    ),

  /** Start the full optimization workflow. Returns batch_id for polling. */
  startWorkflow: (
    orgId: string,
    workflowRequest: Record<string, unknown>,
  ): Promise<ApiResponse<WorkflowStartResponse>> =>
    apiClient.post<WorkflowStartResponse>(
      `${PREFIX}/${orgId}/start`,
      workflowRequest,
    ),

  /** Poll workflow status. */
  getWorkflowStatus: (
    orgId: string,
    batchId: string,
    signal?: AbortSignal,
  ): Promise<ApiResponse<WorkflowStatus>> =>
    apiClient.get<WorkflowStatus>(
      `${PREFIX}/${orgId}/workflows/${batchId}`,
      undefined,
      { signal },
    ),

  /** Apply the reviewed prompt (save as staging). */
  applyReviewedPrompt: (
    orgId: string,
    batchId: string,
    body: ApplyRequest,
  ): Promise<ApiResponse<WorkflowStartResponse>> =>
    apiClient.post<WorkflowStartResponse>(
      `${PREFIX}/${orgId}/workflows/${batchId}/apply`,
      body,
    ),
};

export default versionWorkflowService;
